import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdsAdvertiserItem } from '@brand24-original/llm-types';
import CheckboxSelect, {
  CheckboxSelectOption,
} from '../../../shared/components/checkbox-select/CheckboxSelect';
import { AdvertiserFavicon } from './AdsTableParts';

interface AdsAdvertiserSelectProps {
  advertisers: AdsAdvertiserItem[];
  /** Selected normalized advertiser domains; empty = all advertisers (no filter). */
  selected: string[];
  onChange: (domains: string[]) => void;
}

/** Option/trigger row: favicon (or globe for "All") + name — the model dropdown's row shape. */
const AdvertiserRow: React.FC<{ advertiser: AdsAdvertiserItem | null; label: string }> = ({
  advertiser,
  label,
}) => (
  <div className="dropdown-item">
    {advertiser ? (
      <AdvertiserFavicon
        faviconUrl={advertiser.faviconUrl}
        domain={advertiser.domain}
        className="dropdown-item__icon"
      />
    ) : (
      <span className="dropdown-item__icon ico--xs ico--globe" />
    )}
    <div className="p-dropdown-item">{label}</div>
  </div>
);

/**
 * Advertiser filter — the same checkbox list as the model filter (`CheckboxSelect`), so several
 * advertisers can be compared at once. "All" / every box ticked = no filter (the page sends no
 * `brands=` param); the list is searchable because a project can carry 100+ advertisers.
 */
const AdsAdvertiserSelect: React.FC<AdsAdvertiserSelectProps> = ({ advertisers, selected, onChange }) => {
  const { t } = useTranslation();

  const allDomains = useMemo(() => advertisers.map((advertiser) => advertiser.domain), [advertisers]);
  const effective = useMemo(() => {
    const known = selected.filter((domain) => allDomains.includes(domain));
    return known.length > 0 ? known : allDomains;
  }, [selected, allDomains]);
  const isAllSelected = effective.length >= allDomains.length;

  const options = useMemo<CheckboxSelectOption<string>[]>(
    () =>
      advertisers.map((advertiser) => ({
        value: advertiser.domain,
        label: advertiser.advertiser,
        content: <AdvertiserRow advertiser={advertiser} label={advertiser.advertiser} />,
      })),
    [advertisers]
  );

  const single = effective.length === 1 ? advertisers.find((a) => a.domain === effective[0]) ?? null : null;
  const trigger = isAllSelected ? (
    <AdvertiserRow advertiser={null} label={t('ads.allAdvertisers')} />
  ) : single ? (
    <AdvertiserRow advertiser={single} label={single.advertiser} />
  ) : (
    <AdvertiserRow advertiser={null} label={`${effective.length} ${t('ads.advertisersSelected')}`} />
  );

  if (advertisers.length === 0) {
    return null;
  }

  return (
    <CheckboxSelect
      selected={effective}
      onChange={(next) => onChange(next.length >= allDomains.length ? [] : next)}
      options={options}
      allLabel={t('ads.allAdvertisers')}
      allContent={<AdvertiserRow advertiser={null} label={t('ads.allAdvertisers')} />}
      triggerContent={trigger}
      searchPlaceholder={t('ads.searchAdvertisers')}
      maxListHeight={320}
      className="ads-page__advertiser-select"
    />
  );
};

export default AdsAdvertiserSelect;
